import React, { useCallback, useMemo } from 'react';
import type { PageDetailOut } from '../../types/catalog';
import { Volume2 } from 'lucide-react';
import { speakTamil } from '../../utils/tts';
import { ImageWithFallback } from '@/components/common/ImageWithFallback';

/** Item shape: word only (bullets) or future { text, image?: { url } } */
type WordItem = string | { text?: string; image?: { url?: string } };

interface VocabPictureLabelsV1Props {
  page: PageDetailOut;
}

function getBlocks(page: PageDetailOut) {
  const content = page.page_json_v1?.content as { blocks?: { type?: string; text?: string; items?: unknown[] }[] } | undefined;
  return content?.blocks ?? [];
}

/** Title: heading block text, else page_title */
function getTitle(page: PageDetailOut): string {
  const blocks = getBlocks(page);
  const heading = blocks.find((b) => (b.type ?? '').toLowerCase() === 'heading');
  if (heading?.text?.trim()) return heading.text.trim();
  return (page.page_title ?? (page.page_json_v1 as { page_title?: string })?.page_title ?? 'Vocabulary') as string;
}

/**
 * Vocabulary words: prefer content.items (future), else bullets.items, else derive from sections.
 * Always returns array of { word: string, imageUrl?: string }.
 */
function getWords(page: PageDetailOut): { word: string; imageUrl?: string }[] {
  const content = page.page_json_v1?.content as {
    items?: WordItem[];
    blocks?: { type?: string; items?: string[] }[];
    sections?: { type?: string; items?: { text?: string; word?: string }[] }[];
  } | undefined;

  // Future: item-level image from content.items
  if (Array.isArray(content?.items) && content.items.length > 0) {
    return content.items.map((item) => {
      if (typeof item === 'string') {
        return { word: item.trim() };
      }
      const text = (item.text ?? '').trim();
      const url = typeof item.image === 'object' && item.image?.url ? item.image.url : undefined;
      return { word: text, imageUrl: url };
    }).filter((x) => x.word);
  }

  // Current: bullets block
  const blocks = getBlocks(page);
  const bullets = blocks.find((b) => (b.type ?? '').toLowerCase() === 'bullets');
  const bulletItems = bullets?.items;
  if (Array.isArray(bulletItems)) {
    return bulletItems.map((it) => ({
      word: typeof it === 'string' ? it.trim() : String(it ?? '').trim(),
    })).filter((x) => x.word);
  }

  // Optional fallback: sections with list-like items
  const sections = content?.sections ?? [];
  for (const sec of sections) {
    const items = (sec as { items?: { text?: string; word?: string }[] }).items;
    if (Array.isArray(items) && items.length > 0) {
      return items.map((i) => ({ word: (i.text ?? i.word ?? '').trim() })).filter((x) => x.word);
    }
  }

  return [];
}

export const VocabPictureLabelsV1: React.FC<VocabPictureLabelsV1Props> = ({ page }) => {
  const title = useMemo(() => getTitle(page), [page]);
  const words = useMemo(() => getWords(page), [page]);

  const playTts = useCallback((text: string) => {
    if (text?.trim()) speakTamil(text.trim());
  }, []);

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
        {title}
      </h1>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {words.map((item, i) => (
          <div
            key={item.word + i}
            className="rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-gray-800 shadow-sm overflow-hidden flex flex-col hover:shadow-md transition-shadow"
          >
            <div className="h-40 bg-slate-100 dark:bg-slate-700/50 shrink-0">
              <ImageWithFallback
                src={item.imageUrl ?? ''}
                alt={item.word}
                className="h-full w-full object-cover"
              />
            </div>
            <div className="p-3 flex items-center justify-between gap-2 min-h-[3rem]">
              <span className="text-lg font-semibold text-slate-900 dark:text-white flex-1 truncate">
                {item.word}
              </span>
              <button
                type="button"
                onClick={() => playTts(item.word)}
                aria-label={`Play: ${item.word}`}
                className="h-10 w-10 rounded-full bg-sky-100 dark:bg-sky-900/50 text-sky-700 dark:text-sky-300 flex items-center justify-center hover:bg-sky-200 dark:hover:bg-sky-800/50 transition-colors shrink-0"
              >
                <Volume2 className="w-5 h-5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {words.length === 0 && (
        <p className="text-gray-500 dark:text-gray-400">No vocabulary items on this page.</p>
      )}
    </div>
  );
};
